import React, { useState } from "react";
import { Modal } from "antd";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../../redux/actions/toast";
import ajaxMethods from "../../../../../api/ajax-methods";
import * as ajaxEndpoints from "../../../../../api/ajax-endpoints";
import { ErrorLoader } from "../../../../../UI/Loaders/Loaders";
import { IPendingWalletCreationRequest } from "../../hooks/usePendingWalletCreationRequests";
import moment from "moment";
import { appInsights } from "../../../../../config/appInsights";

interface IApproveWalletModalProps {
  visible: boolean;
  onClose: () => void;
  walletData: IPendingWalletCreationRequest | null;
  onSuccess: () => void;
}

type LoadingState = "confirm" | "loading" | "success" | "error";

const ApproveWalletModal: React.FC<IApproveWalletModalProps> = ({
  visible,
  onClose,
  walletData,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [auth, setAuth] = useState("");
  const [loadingState, setLoadingState] = useState<LoadingState>("confirm");

  const handleApprove = async () => {
    if (!walletData) return;

    setLoadingState("loading");

    try {
      const response = await ajaxMethods.post(
        ajaxEndpoints.APPROVE_WALLET_CREATION_REQUEST,
        {
          ...walletData,
          comment,
          otp: auth,
        }
      );

      if (response.status === 200) {
        setLoadingState("success");
        setTimeout(() => {
          dispatch(
            setToast(
              "success",
              response?.data?.message || "Wallet creation approved successfully"
            )
          );
          onSuccess();
          handleClose();
        }, 2000);
      } else {
        setLoadingState("error");
        setTimeout(() => {
          dispatch(
            setToast(
              "warning",
              response?.data?.message || "Unable to approve wallet creation"
            )
          );
          handleClose();
        }, 3000);
      }
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "ApproveWalletModal.tsx",
        },
      });
      setLoadingState("error");
      setTimeout(() => {
        dispatch(setToast("warning", "Unable to connect to Advancly services"));
        handleClose();
      }, 3000);
    }
  };

  const handleClose = () => {
    setComment("");
    setAuth("");
    setLoadingState("confirm");
    onClose();
  };

  if (!walletData) return null;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      closable={loadingState === "confirm"}
      maskClosable={loadingState === "confirm"}
    >
      <div className="Modal__body">
        {loadingState === "confirm" && (
          <>
            <h3 className="Modal__title p-b-1">Approval Summary</h3>
            <div className="m-b-30">
              <h5>
                Are you sure you want to approve the following wallet creation
                request?
              </h5>

              <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                <p>
                  <strong>Target ID:</strong> {walletData.targetId}
                </p>
                <p>
                  <strong>Consumer Type:</strong> {walletData.consumerType}
                </p>
                <p>
                  <strong>Client Type:</strong> {walletData.clientType}
                </p>
                <p>
                  <strong>Currency:</strong> {walletData.currency}
                </p>
                <p>
                  <strong>Provider Type:</strong> {walletData.providerType}
                </p>
                <p>
                  <strong>Effective Date:</strong>{" "}
                  {moment(walletData.effectiveDate).format("DD-MM-YYYY")}
                </p>
                <p>
                  <strong>Note:</strong> {walletData.note || "N/A"}
                </p>
              </div>

              <form>
                <label style={{ width: "100%", marginBottom: "1rem" }}>
                  <strong>Comment</strong>
                  <input
                    className="form-control"
                    name="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Reason for approving this wallet creation"
                  />
                </label>
                <label style={{ width: "50%" }}>
                  <strong>Generated 2FA code</strong>
                  <input
                    className="form-control"
                    name="auth"
                    value={auth}
                    onChange={(e) => setAuth(e.target.value)}
                    placeholder="Enter 2FA code"
                  />
                </label>
              </form>

              <div className="Form__action-buttons m-t-2">
                <button
                  type="submit"
                  onClick={handleApprove}
                  disabled={!comment || !auth}
                  className="btn zoom-element btn login-btn m-0 mr-auto"
                >
                  <i className="fa fa-check-circle" /> Proceed
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn zoom-element btn login-btn m-0 ml-auto"
                >
                  <i className="fa fa-times-circle" /> Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {loadingState === "loading" && (
          <div className="loading-overlay speed-1x fadeInRight">
            <div className="form-loader form-loader-login verify">
              <div className="centerLoader">
                <div className="loading" id="adv-loader-alt" />
                <div className="center-txt webapp-ripple-text">
                  <i className="notifier fas fa-user-cog" />
                  Processing Approval...
                </div>
              </div>
            </div>
          </div>
        )}

        {loadingState === "success" && (
          <div className="loading-overlay speed-1x fadeInRight">
            <div className="form-loader form-loader-login verify">
              <div className="centerLoader">
                <div className="loading success-loader">
                  <i className="webapp-ripple-notification success-text fa fa-check-circle" />
                </div>
                <div className="center-txt webapp-ripple-text">
                  <i className="notifier far fa-check-circle" />
                  Approval Successful
                </div>
              </div>
            </div>
          </div>
        )}

        {loadingState === "error" && (
          <div className="loading-overlay speed-1x fadeInRight">
            <ErrorLoader
              icon="fas fa-wifi"
              text="Network Issue"
              subText="Unable to connect to Advancly services."
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ApproveWalletModal;
